import { NetworkStatus } from "@apollo/client";

import { useOrgPositionsQuery } from "../graphql";

interface UsePositionsPaginationOptions {
  searchTerm?: string;
  atsOnly?: boolean;
  activeOnly?: boolean;
  limit?: number;
  onError?: (error: Error) => void;
}

export const usePositionsPagination = ({
  searchTerm = "",
  atsOnly,
  activeOnly,
  limit = 20,
  onError,
}: UsePositionsPaginationOptions = {}) => {
  const { data, loading, error, fetchMore, networkStatus } =
    useOrgPositionsQuery({
      variables: {
        atsOnly,
        activeOnly,
        pagination: {
          limit,
        },
        searchTerm,
      },
      notifyOnNetworkStatusChange: true,
      onError,
    });

  const positions = data?.currentUser?.organization.positionsV2.results || [];
  const pageInfo = data?.currentUser?.organization.positionsV2.pageInfo;
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  // Compute hasMoreData directly from the data
  const hasMoreData = pageInfo?.hasNextPage;

  const loadMorePositions = () => {
    if (!hasMoreData || isLoadingMore) return;

    fetchMore({
      variables: {
        pagination: {
          after: pageInfo?.endCursor,
          limit,
        },
        searchTerm,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        if (!prev.currentUser) return prev;

        const newPositions =
          fetchMoreResult.currentUser?.organization.positionsV2.results || [];
        const newPageInfo =
          fetchMoreResult.currentUser?.organization.positionsV2.pageInfo;

        return {
          ...prev,
          currentUser: {
            ...prev.currentUser,
            organization: {
              ...prev.currentUser?.organization,
              positionsV2: {
                ...prev.currentUser?.organization.positionsV2,
                pageInfo: newPageInfo,
                results: [
                  ...(prev.currentUser?.organization.positionsV2.results || []),
                  ...newPositions,
                ],
              },
            },
          },
        };
      },
    });
  };

  return {
    positions,
    loading,
    error,
    loadMorePositions,
    hasMoreData,
    isLoadingMore,
    pageInfo,
  };
};
